import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Badge, Spinner, Empty } from "../ui";
import { useAuth, usePermissions } from "../AuthContext";
import { Building2, Plus, Power, PowerOff, X, LogIn, Trash2, Eye, FileText, PencilRuler } from "lucide-react";
import { toast } from "sonner";

function TenantModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", slug: "", admin_email: "", admin_password: "", admin_name: "Admin" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/tenants", form); toast.success("Tenant creat"); onSaved(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Tenant nou</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={save} data-testid="tenant-form">
          <Field label="Nume companie *"><Input data-testid="tenant-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Slug (subdomeniu) *"><Input data-testid="tenant-slug-input" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} placeholder="firma-mea" /></Field>
          <Field label="Email admin *"><Input data-testid="tenant-admin-email-input" type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></Field>
          <Field label="Parola admin *"><Input data-testid="tenant-admin-password-input" type="password" required minLength={6} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn type="submit" disabled={saving} data-testid="save-tenant-button">Creeaza</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default function Tenants() {
  const { setTenantId, tenantId } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenants"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    const action = t.status === "active" ? "deactivate" : "activate";
    try { await api.post(`/tenants/${t.id}/${action}`); toast.success("Actualizat"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Stergi definitiv tenantul "${t.name}" (${t.slug}) si toate datele lui? Actiunea este ireversibila.`)) return;
    try {
      if (tenantId === t.id) setTenantId("");
      await api.delete(`/tenants/${t.id}`);
      toast.success("Tenant sters");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const previewSite = (t) => {
    window.open(`${window.location.origin}/site/${t.slug}`, "_blank");
  };

  const viewProforme = (t) => {
    setTenantId(t.id);
    navigate("/proforme");
  };

  const editSite = (t) => {
    setTenantId(t.id);
    toast.success(`Editezi pagina: ${t.name}`);
    navigate("/site");
  };

  return (
    <div>
      <PageHeader title="Tenanti" subtitle="Gestioneaza companiile din platforma (doar super admin)."
        actions={can("tenanti", "add") ? <Btn onClick={() => setShowAdd(true)} data-testid="add-tenant-button"><Plus className="w-4 h-4" /> Tenant nou</Btn> : null} />

      {loading ? <Spinner /> : items.length === 0 ? (
        <Card><Empty icon={Building2} title="Niciun tenant" hint="Creeaza prima companie." /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tenants-grid">
          {items.map((t) => (
            <Card key={t.id} className={`p-5 ${tenantId === t.id ? "ring-2 ring-emerald-500" : ""}`} data-testid={`tenant-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-slate-600" /></div>
                <Badge color={t.status === "active" ? "green" : "red"}>{t.status}</Badge>
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{t.name}</h3>
              <p className="text-sm text-slate-400">{t.slug}.yvola.eu</p>
              {t.admin_email && (
                <p className="text-xs text-slate-500 mt-1" data-testid={`tenant-admin-email-${t.id}`}>
                  Creat de: {t.admin_email}
                </p>
              )}
              <div className="mt-4 flex gap-2">
                <Btn variant="outline" className="flex-1" onClick={() => { setTenantId(t.id); toast.success(`Context: ${t.name}`); }} data-testid={`select-tenant-${t.id}`}>
                  <LogIn className="w-4 h-4" /> {tenantId === t.id ? "Selectat" : "Selecteaza"}
                </Btn>
                <Btn variant={t.status === "active" ? "outline" : "primary"} onClick={() => toggle(t)} data-testid={`toggle-tenant-${t.id}`}>
                  {t.status === "active" ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                </Btn>
                {can("tenanti", "delete") && (
                  <Btn variant="danger" onClick={() => remove(t)} data-testid={`delete-tenant-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Btn>
                )}
              </div>
              <div className="mt-2 flex gap-2">
                <Btn variant="outline" className="flex-1" onClick={() => previewSite(t)} data-testid={`preview-site-${t.id}`}>
                  <Eye className="w-4 h-4" /> Previzualizare pagina
                </Btn>
                <Btn variant="outline" className="flex-1" onClick={() => viewProforme(t)} data-testid={`view-proforme-${t.id}`}>
                  <FileText className="w-4 h-4" /> Proforme emise
                </Btn>
              </div>
              <div className="mt-2">
                <Btn variant="primary" className="w-full" onClick={() => editSite(t)} data-testid={`edit-site-${t.id}`}>
                  <PencilRuler className="w-4 h-4" /> Editeaza pagina tenant
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showAdd && <TenantModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
